/**
 * Driver OTP Verification Page - Module Inscription Ticket 3
 * Route: /driver/verify
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ArrowLeft, CheckCircle, AlertCircle, RefreshCw, ShieldCheck } from 'lucide-react';
import config from 'config';

const API = `${config.API_URL}/api`;

export default function DriverVerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const inputRefs = useRef([]);

  // Redirect if no email passed
  useEffect(() => {
    if (!email) {
      navigate('/driver/register');
    }
  }, [email, navigate]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < text.length && i < 6; i++) {
        newOtp[i] = text[i];
      }
      setOtp(newOtp);
      const focusIndex = Math.min(text.length, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length < 6) {
      setError('Veuillez entrer le code complet à 6 chiffres');
      return;
    }

    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/driver/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Code invalide');
      }

      // Store tokens
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        localStorage.setItem('user', JSON.stringify(data.user));
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/chauffeur');
        window.location.reload();
      }, 1500);
    } catch (err) {
      setError(err.message);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setResending(true);
    setError('');
    try {
      await fetch(`${API}/auth/driver/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setCooldown(60);
    } catch {
      setError("Erreur lors du renvoi du code");
    } finally {
      setResending(false);
    }
  };

  if (!email) return null;

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Link to="/driver/register" className="inline-flex items-center gap-2 text-[#C9A24D] hover:text-[#d4af5a] transition-colors mb-6" data-testid="back-register-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Retour</span>
          </Link>
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#C9A24D]/10 border border-[#C9A24D]/30 rounded-lg flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-[#C9A24D]" />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Vérification</h1>
          </div>
          <p className="text-[#A0A0A0] text-sm">
            Un code à 6 chiffres a été envoyé à<br />
            <span className="text-white font-medium">{email}</span>
          </p>
        </div>

        <Card className="bg-[#141414] border-[#2A2A2A]">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base text-center">Entrez votre code OTP</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg" data-testid="verify-error">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg" data-testid="verify-success">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <p className="text-green-400 text-sm">Email vérifié ! Redirection...</p>
              </div>
            )}

            <div className="flex justify-center gap-2" onPaste={handlePaste} data-testid="otp-input-group">
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => { inputRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  disabled={loading || success}
                  className="w-11 h-13 text-center text-xl font-bold bg-[#1A1A1A] border border-[#333] text-white rounded-lg focus:border-[#C9A24D] focus:ring-1 focus:ring-[#C9A24D] outline-none transition-all disabled:opacity-50"
                  data-testid={`otp-input-${i}`}
                />
              ))}
            </div>

            <Button
              onClick={handleVerify}
              disabled={loading || success || otp.join('').length < 6}
              className="w-full h-11 bg-[#C9A24D] hover:bg-[#d4af5a] text-black font-semibold"
              data-testid="verify-otp-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : success ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Vérifié
                </>
              ) : (
                'Vérifier'
              )}
            </Button>

            <div className="text-center">
              <button
                onClick={handleResend}
                disabled={resending || cooldown > 0}
                className="text-sm text-[#C9A24D] hover:text-[#d4af5a] disabled:text-[#555] disabled:cursor-not-allowed inline-flex items-center gap-1"
                data-testid="resend-otp-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${resending ? 'animate-spin' : ''}`} />
                {cooldown > 0
                  ? `Renvoyer dans ${cooldown}s`
                  : 'Renvoyer le code'}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
